import { presenceService } from '@doneverse/data/services'
import { useCallback, useEffect, useRef } from 'react'

const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000

interface UseHeartbeatOptions {
  enabled?: boolean
}

export function useHeartbeat({ enabled = true }: UseHeartbeatOptions = {}): void {
  const intervalRef = useRef<number | null>(null)

  const clearHeartbeatInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const sendHeartbeat = useCallback(async () => {
    if (!enabled || document.visibilityState === 'hidden') return

    try {
      await presenceService.sendHeartbeat()
    } catch {
      // Presence should never interrupt the dashboard.
    }
  }, [enabled])

  const startHeartbeatInterval = useCallback(() => {
    clearHeartbeatInterval()

    if (!enabled || document.visibilityState === 'hidden') return

    void sendHeartbeat()
    intervalRef.current = window.setInterval(() => {
      void sendHeartbeat()
    }, HEARTBEAT_INTERVAL_MS)
  }, [clearHeartbeatInterval, enabled, sendHeartbeat])

  useEffect(() => {
    if (!enabled) {
      clearHeartbeatInterval()
      return
    }

    startHeartbeatInterval()

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearHeartbeatInterval()
        return
      }

      startHeartbeatInterval()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearHeartbeatInterval()
    }
  }, [clearHeartbeatInterval, enabled, startHeartbeatInterval])
}
